import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import AuthModal from '../../components/AuthModal/AuthModal';
import { PublicPageCTA, PublicShareLayout, EmptyShareState } from '../../components/PublicShare/PublicShareLayout';
import ShareMenu from '../../components/PublicShare/ShareMenu';
import PublicListsSection from '../../components/MyLists/PublicListsSection';
import TopMoviesProfileSection from '../../components/TopMovies/TopMoviesProfileSection';
import RankingsProfileSection from '../../components/Rankings/RankingsProfileSection';
import useProfileRankings from '../../components/Rankings/useProfileRankings';
import AuthGateAction from '../../components/PublicShare/AuthGateAction';
import { AchievementItem, CollectionTile, PublicCoinTile } from '../../components/PublicShare/PreviewCards';
import { useAuth } from '../../context/AuthContext';
import { useAuthGate } from '../../hooks/useAuthGate';
import { PublicShareService } from '../../services/publicShareService';
import { SocialService } from '../../services/socialService';
import { AUTH_MESSAGES } from '../../utils/authMessages';
import { buildOgImageUrl, useShareMetadata } from '../../utils/shareMetadata';
import styles from '../../components/PublicShare/PublicShare.module.css';
import { FoundingCollectorIcon, FoundingCollectorLabel } from '../../components/FoundingCollectorBadge/FoundingCollectorBadge';

export default function PublicProfileShare() {
    const { username: rawUsername } = useParams();
    const username = rawUsername?.replace(/^@/, '');
    const { user } = useAuth();
    const { gateAction, authModalProps } = useAuthGate(user);
    const [payload, setPayload] = useState(null);
    const [error, setError] = useState(null);
    const [following, setFollowing] = useState(false);
    const profileRankings = useProfileRankings(username, { enabled: Boolean(payload) && !payload.isPrivate });

    useEffect(() => {
        let mounted = true;
        PublicShareService.getProfile(username)
            .then((data) => {
                if (!mounted) return;
                setPayload(data);
                setFollowing(false);
            })
            .catch((err) => mounted && setError(err.message || 'Profile not found'));
        return () => { mounted = false; };
    }, [username]);

    const share = useMemo(() => {
        const handle = payload?.user?.username || username;
        const stats = payload?.stats;
        return {
            url: `${window.location.origin}/u/${handle}`,
            title: `@${handle}’s Verified Movie & TV Profile | Aluvo`,
            description: stats
                ? `${stats.totalVerified} verified titles. ${stats.legendaryCount} Legendary coins. ${stats.completedCollectionsCount} completed collections.`
                : `Check out @${handle}’s verified movie & TV profile on Aluvo.`,
            text: `Check out @${handle}’s verified movie & TV profile on Aluvo.`,
            image: buildOgImageUrl({ title: `@${handle}`, subtitle: 'Verified movie & TV profile', username: handle }),
        };
    }, [payload, username]);

    useShareMetadata(share);

    if (error) return <EmptyShareState icon="👤" title="Profile not found" message="This collector profile is not available." />;
    if (!payload) return <EmptyShareState icon="◎" title="Loading trophy case" message="Gathering verified coins and collections..." />;
    if (payload.isPrivate) return <EmptyShareState icon="🔒" title="This collector’s profile is private." message="Their Aluvo trophy case is not public right now." />;

    const { user: profileUser, stats, featuredCoins = [], recentAchievements = [], collectionHighlights = [], publicLists = [], topMovies = null } = payload;
    const avatarInitial = (profileUser.displayName || profileUser.username || '?').charAt(0).toUpperCase();

    async function followUser() {
        if (!profileUser?.id) return;
        if (following) {
            await SocialService.unfollow(profileUser.id);
            setFollowing(false);
        } else {
            await SocialService.follow(profileUser.id);
            setFollowing(true);
        }
    }

    return (
        <PublicShareLayout>
            <section className={styles.heroCard}>
                <div>
                    <div className={styles.profileIdentity}>
                        {profileUser.avatarUrl ? <img className={styles.avatar} src={profileUser.avatarUrl} alt={profileUser.displayName || profileUser.username} /> : <div className={styles.avatar}>{avatarInitial}</div>}
                        <div>
                            <span className={styles.statusPill}>{payload.statusLabel}</span>
                            <h1 className={styles.title}>
                                @{profileUser.username}
                                <FoundingCollectorIcon isFoundingCollector={profileUser.isFoundingCollector} size="md" />
                            </h1>
                            {profileUser.displayName && <p className={styles.displayName}>{profileUser.displayName}</p>}
                            <FoundingCollectorLabel isFoundingCollector={profileUser.isFoundingCollector} />
                        </div>
                    </div>
                    <p className={styles.subtitle}>This is @{profileUser.username}’s verified movie & TV identity — coins earned by watching, proving, upgrading, and completing collections.</p>
                    <div className={styles.statGrid}>
                        <Stat value={stats.totalVerified} label="Verified titles" />
                        <Stat value={stats.totalCoins} label="Total coins" />
                        <Stat value={stats.legendaryCount} label="Legendary" />
                        <Stat value={stats.goldCount} label="Gold" />
                        <Stat value={stats.completedCollectionsCount} label="Completed collections" />
                    </div>
                    <div className={styles.heroActions}>
                        <ShareMenu {...share} compact />
                        <AuthGateAction className={styles.secondaryAction} gateAction={gateAction} authMessage={AUTH_MESSAGES.FOLLOW} onClick={followUser}>
                            {following ? 'Following' : 'Follow user'}
                        </AuthGateAction>
                        <a className={styles.ghostAction} href="#coins">View Coins</a>
                        <a className={styles.ghostAction} href="#collections">View Collections</a>
                    </div>
                </div>
                <div className={styles.panel}>
                    <span className={styles.eyebrow}>Public proof</span>
                    <h2>Earned, not bought.</h2>
                    <p className={styles.muted}>Every coin here represents a verified watch or collection unlock in Aluvo’s progression loop.</p>
                </div>
            </section>

            <section id="coins" className={styles.panel}>
                <h2>Featured coins</h2>
                <div className={styles.grid}>{featuredCoins.map((coin) => <PublicCoinTile key={coin.id} coin={coin} />)}</div>
            </section>

            <section className={styles.panel}>
                <h2>Recent public achievements</h2>
                <div className={styles.grid}>{recentAchievements.map((achievement) => <AchievementItem key={achievement.id} achievement={achievement} />)}</div>
            </section>

            <section id="collections" className={styles.panel}>
                <h2>Collection highlights</h2>
                <div className={styles.grid}>{collectionHighlights.map((collection) => <CollectionTile key={collection.id} collection={{ ...collection, title: collection.title, user: profileUser }} />)}</div>
                {!collectionHighlights.length && <p className={styles.muted}>No completed public collections yet.</p>}
            </section>

            <TopMoviesProfileSection topMovies={topMovies} className={styles.panel} />

            <RankingsProfileSection
                items={profileRankings.items}
                hasMore={profileRankings.hasMore}
                seeAllTo={`/user/${encodeURIComponent(profileUser.username)}/rankings`}
                sourceScreen="public_profile"
                loggedIn={Boolean(user)}
                className={styles.panel}
            />

            <PublicListsSection lists={publicLists} username={profileUser.username} className={styles.panel} />

            <PublicPageCTA
                title="Create your Aluvo profile and start earning coins."
                text="Watch, verify, earn coin, upgrade, unlock Legendary, complete collections, and share your trophy case."
            />
            <AuthModal {...authModalProps} />
        </PublicShareLayout>
    );
}

function Stat({ value, label }) {
    return <div className={styles.stat}><strong>{value ?? 0}</strong><span>{label}</span></div>;
}
